import { PROFESSION_OPTIONS } from '../constants/EmrNoteTypeMapping';

export const getAdoptionColor = (label) => {
  const key = String(label || '').toLowerCase();
  if (key === 'invited') return 'info';
  if (key === 'trying it out') return 'info';
  if (key === 'getting the hang of it') return 'primary';
  if (key === 'power user') return 'success';
  if (key === 'potentially failed onboarding') return 'danger';
  if (key === 'failed onboarding') return 'danger';
  if (key === 'churned') return 'danger';
  return 'secondary';
};

export const adoptionTooltipText =
  'All stages use a rolling 30-day visit window — not all-time counts.\n' +
  'Invited: 0 visits in the last 30 days.\n' +
  'Trying It Out: 1–10 visits (rolling 30d) and used ≤5 days ago.\n' +
  'Getting the Hang of It: 11–30 visits (rolling 30d) and used ≤7 days ago.\n' +
  'Power User: >30 visits (rolling 30d) and used ≤14 days ago.\n' +
  'At Risk: has past activity but recency or rolling volume thresholds not met.\n' +
  'Churned: last activity >14 days ago.\n' +
  'WoW arrows (↑↓) show this week vs last week visit volume.';

export const normalizeTimestampToDate = (timestampString) => {
  if (!timestampString) return null;
  let raw = String(timestampString).trim();
  if (!raw.includes('T')) raw = raw.replace(' ', 'T');
  if (!/[zZ]|[+-]\d\d:?\d\d$/.test(raw)) raw += 'Z';
  const dt = new Date(raw);
  return isNaN(dt.getTime()) ? null : dt;
};

export const daysDiffFromToday = (date) => {
  if (!date) return Infinity;
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfThatDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  return Math.floor(
    (startOfToday.getTime() - startOfThatDay.getTime()) / (24 * 60 * 60 * 1000),
  );
};

export const computeGroupAdoption = (stat) => {
  let totalVisits = 0;
  let mostRecent = null;
  let mostRecentRaw = null;
  if (stat && stat.users) {
    Object.values(stat.users).forEach((details) => {
      const visits =
        typeof details?.credit_usage === 'number' ? details.credit_usage : 0;
      totalVisits += visits;
      const dt = normalizeTimestampToDate(details?.updated_at);
      if (dt && (!mostRecent || dt > mostRecent)) {
        mostRecent = dt;
        mostRecentRaw = details?.updated_at;
      }
    });
  }

  if (!totalVisits || totalVisits === 0) {
    const label = 'Invited';
    return {
      label,
      color: getAdoptionColor(label),
      totalVisits,
      mostRecent: mostRecentRaw,
    };
  }

  const diffDays = daysDiffFromToday(mostRecent);

  if (totalVisits <= 20 && diffDays <= 5) {
    const label = 'Trying It Out';
    return {
      label,
      color: getAdoptionColor(label),
      totalVisits,
      mostRecent: mostRecentRaw,
    };
  }
  if (totalVisits > 20 && totalVisits <= 50 && diffDays <= 7) {
    const label = 'Getting the Hang of It';
    return {
      label,
      color: getAdoptionColor(label),
      totalVisits,
      mostRecent: mostRecentRaw,
    };
  }
  if (totalVisits > 50 && diffDays <= 14) {
    const label = 'Power User';
    return {
      label,
      color: getAdoptionColor(label),
      totalVisits,
      mostRecent: mostRecentRaw,
    };
  }
  if (diffDays > 7) {
    const label = 'Potentially Failed Onboarding';
    return {
      label,
      color: getAdoptionColor(label),
      totalVisits,
      mostRecent: mostRecentRaw,
    };
  }
  {
    const label = 'Trying It Out';
    return {
      label,
      color: getAdoptionColor(label),
      totalVisits,
      mostRecent: mostRecentRaw,
    };
  }
};

export const computeUserAdoption = (details) => {
  const visits =
    typeof details?.credit_usage === 'number' ? details.credit_usage : 0;
  const dt = normalizeTimestampToDate(details?.updated_at);
  const diffDays = daysDiffFromToday(dt);

  if (diffDays > 14) {
    const label = 'Churned';
    return { label, color: getAdoptionColor(label) };
  }
  if (!visits || visits === 0) {
    const label = 'Invited';
    return { label, color: getAdoptionColor(label) };
  }
  if (visits <= 20 && diffDays <= 5) {
    const label = 'Trying It Out';
    return { label, color: getAdoptionColor(label) };
  }
  if (visits > 20 && visits <= 50 && diffDays <= 7) {
    const label = 'Getting the Hang of It';
    return { label, color: getAdoptionColor(label) };
  }
  if (visits > 50 && diffDays <= 14) {
    const label = 'Power User';
    return { label, color: getAdoptionColor(label) };
  }
  if (diffDays > 7) {
    const label = 'Potentially Failed Onboarding';
    return { label, color: getAdoptionColor(label) };
  }
  {
    const label = 'Trying It Out';
    return { label, color: getAdoptionColor(label) };
  }
};

export const formatLastUsedPretty = (timestampString) => {
  if (!timestampString) return '';
  const dt = normalizeTimestampToDate(timestampString);
  if (!dt) return String(timestampString);
  const diff = daysDiffFromToday(dt);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff <= 7) return dt.toLocaleDateString(undefined, { weekday: 'long' });
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

export const makeSafeId = (raw) =>
  String(raw || '')
    .replace(/[^A-Za-z0-9_-]/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

// Saved professions (users[].profession from group stats), for the profession filter (COM-612)
export const getUniqueProfessions = (users) =>
  Array.from(
    new Set(
      (users || [])
        .map((user) => String(user?.profession || '').trim())
        .filter(Boolean),
    ),
  ).sort();

// Full profession list, plus any saved legacy values (e.g. "Physical Therapy") so those users stay filterable
export const getProfessionFilterOptions = (savedProfessions) => [
  ...PROFESSION_OPTIONS,
  ...(savedProfessions || []).filter((p) => !PROFESSION_OPTIONS.includes(p)),
];

// Empty selection = all professions
export const filterUsersByProfession = (users, profession) => {
  if (!profession) return users || [];
  return (users || []).filter(
    (user) => String(user?.profession || '').trim() === profession,
  );
};
